import React, { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useOutletContext } from 'react-router'

import { skipToken } from '@reduxjs/toolkit/query'
import { get, isEmpty, pick, reduce } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { isUnclosedSessionStatus } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/constants'
import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { getAppointmentTimeslot } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import {
  getProgramDisplayLabel,
  getProgramNameFromId,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  DBT_TEENS_SESSION_TYPE_LABELS,
  LTSessionTypes,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import { isInitialEvaluationSessionType } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  AppointmentSummary,
  noteSummaryStatusText,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { Content } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { LTProgramName, ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { TransferStatus } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { DAInvoiceFailedModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/daInvoiceFailedModal/DAInvoiceFailedModal'
import { DBTTeensSessionTypeIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dbtTeensSessionTypeIcon/DBTTeensSessionTypeIcon'
import { NotesList, NotesListItem } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/notesList/NotesList'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { addLyraSelectClientNoticeToNote } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/notes/utils'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { useGetAppointmentSummariesQuery, useGetNotesQuery } from './data/notesApi'
import { prepopulationFieldsMap } from './data/NotesPrepopulationMap'
import { useCompletedOrResubmittedAppts } from './hooks/useDAAppointmentSets'
import { DA_FLEXIBLE_NOTE_NAME } from './utils/noteConstants'
import { getPendingChargeNavState } from './utils/noteNavigationUtils'
import { getGeneralNoteTitle } from './utils/noteTitleUtils'
import { getNpSessionNoteExternalValues, NP_SESSION_NOTE_NAME } from './utils/npSessionNote'
import { actions, mixpanelEvents, sessionNoteTypes } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { programConfig } from '../../common/constants/appConstants'
import { CLIENT_NEW_NOTE, CLIENT_NOTE } from '../../common/constants/routingConstants'
import { logToSentry, resolveDAEngageProgramName } from '../../common/utils/utils'
import { getAuthConfig, getAuthUserId } from '../../data/auth/authSelectors'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsData } from '../../data/lyraTherapy/contentSelectors'
import { useIsEngageDAProvider } from '../../hooks/useIsEngageDAProvider'
import { useGetTransfersQuery } from '../../payments/data/transfersApi'
import { useAssignmentsData } from '../assignments/data/hooks/useAssignmentsData'
import ClientNotesPDF from '../clients/clientDetails/clientRecords/ClientNotesPDF'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { useEpisodesForSelectedClient } from '../episodes/data/hooks/useEpisodesForSelectedClient'

type ClientNotesProps = {
  setNotesPDFContent?: (notesPDFContent: any) => void
  pdfNotesEpisodeId?: string
  pdfContentStartDate?: string
  pdfContentEndDate?: string
}

type NoteWithNoteListItemProperties = Note & Omit<NotesListItem, keyof Note>

const NotesSectionContainer = styled.View<{ theme: ThemeType; isDBTTeens?: boolean; isLastSection?: boolean }>(
  ({
    theme: { breakpoints, spacing },
    isDBTTeens,
    isLastSection,
  }: {
    theme: ThemeType
    isDBTTeens?: boolean
    isLastSection?: boolean
  }) =>
    isDBTTeens
      ? {
          width: '100%',
          paddingTop: spacing['40px'],
          paddingLeft: spacing['40px'],
          paddingRight: spacing['40px'],
          paddingBottom: isLastSection ? spacing['40px'] : 0,
        }
      : {
          margin: 'auto',
          marginTop: spacing['24px'],
          marginBottom: spacing['48px'],
          width: breakpoints.isMinWidthTablet ? '750px' : '100%',
        },
)

const SubheaderContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['24px'],
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
}))

const NotesTitle = styled(Title)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['8px'],
}))

export const populateInitialValues = (formResponses: any[] = [], name: any, contents: Content[]) => {
  const prefillableField = (originWidget: any, destinationWidget: any) => {
    if (originWidget === destinationWidget || destinationWidget === 'text' || destinationWidget === 'textarea') {
      return true
    } else {
      return false
    }
  }

  const formUiSchema = contents.find((n: any) => n.name === name)?.meta_data?.uiSchema || {}
  if (isEmpty(formUiSchema)) return
  let initialValues = {}
  formResponses.forEach(({ contentName, data, responseUiSchema = {} }) => {
    const fieldsMap = ((prepopulationFieldsMap as Record<string, Record<string, any>>)[name] || {})[contentName]
    if (!fieldsMap) {
      return
    }
    let valuesToPopulate = pick(data, Object.keys(fieldsMap))
    // Use the fieldsMap to swap the field keys to the form we are prepopulating
    valuesToPopulate = reduce(
      valuesToPopulate,
      function (res, val, key) {
        let keyValuePairs = {}
        // check to see if fieldsMap[key] is an array in case of a 1:many prepopulation map
        if (Array.isArray(fieldsMap[key]))
          keyValuePairs = fieldsMap[key].reduce((obj: any, targetFieldName: any) => {
            if (
              key in responseUiSchema &&
              targetFieldName in formUiSchema &&
              prefillableField(responseUiSchema[key]['ui:widget'], formUiSchema[targetFieldName]['ui:widget'])
            ) {
              return { ...obj, [targetFieldName]: val }
            } else {
              return obj
            }
          }, {})
        else {
          if (
            key in responseUiSchema &&
            fieldsMap[key] in formUiSchema &&
            prefillableField(responseUiSchema[key]['ui:widget'], formUiSchema[fieldsMap[key]]['ui:widget'])
          ) {
            keyValuePairs = { [fieldsMap[key]]: val }
          }
        }
        return { ...res, ...keyValuePairs }
      },
      {},
    )
    initialValues = { ...initialValues, ...valuesToPopulate }
  })
  return initialValues
}

// Router state is structured-cloned into history, which cannot clone React elements, so the
// view-only session type icon has to come off the note before navigating.
const toNavigableNote = ({ sessionTypeIcon: _sessionTypeIcon, ...note }: NotesListItem) => note

const ClientNotes: React.FC<ClientNotesProps> = ({
  setNotesPDFContent,
  pdfNotesEpisodeId,
  pdfContentStartDate,
  pdfContentEndDate,
}) => {
  const intl = useIntl()
  const {
    enableAINotesFeatures,
    showAINotesFeatures,
    showAINotesForLCMAndCLE,
    enableExportClientRecordsSentryLogging: enableExportClientRecordsLogging,
    enableDAFlexibleNote,
    isDBTTeensEnabled,
  } = useFlags()

  const { data: clientDetails } = useSelectedClientData()

  const { data: assignments } = useAssignmentsData()
  const clientId = useSelector(getSelectedClientId)
  const isLyraSelectClient = clientDetails?.product_type === PRODUCT_TYPE.SELECT
  const contents = useSelector(getLyraTherapyContentsData)

  const { data: episodes } = useEpisodesForSelectedClient()

  // The outlet context is defined in ClientNotesRoutes and will always provide
  // the correct episode - either from the redesigned client home state or the pre-redesign redux selected episode
  const { episode, lastProviderSpecificEpisode } = useOutletContext<{
    episode: Episode | null
    lastProviderSpecificEpisode: Episode | undefined
  }>()
  const clientEpisodeProgramConfig = episode?.program_name ? programConfig[episode.program_name] : undefined

  const contentsLoaded = !!contents && contents.length > 0
  const noteContentMetadataName = episode?.program_name
    ? Programs.getProgramConfigByName(episode.program_name)?.noteContentMetadataName
    : undefined
  const hasRequiredNoteMetadata = contentsLoaded && !!noteContentMetadataName

  const { data: allAppointments = [] } = useClientAppointmentsData()
  const sessions = allAppointments.filter((session: Appointment) =>
    ['rescheduled', 'newAppt', 'concluded', 'completed'].includes(session.appointmentStatus),
  )
  const userId = useSelector(getAuthUserId)
  const openClientAccessSupported = useSelector(getAuthConfig)?.openClientAccessSupported ?? false
  const isDAEngageProvider = useIsEngageDAProvider()

  const appointmentsInfoForPDF = useMemo(() => {
    return allAppointments?.map((appointment: Appointment) => {
      const appointmentTimeslot = getAppointmentTimeslot({ appointment, intl })
      return {
        appointmentId: appointment.appointmentId,
        appointmentTimeslot,
        sessionNumber: appointment?.sessionNumber,
      }
    })
  }, [allAppointments, intl])

  const {
    data: notes,
    isLoading,
    isSuccess,
  } = useGetNotesQuery(
    pdfNotesEpisodeId ? { episodeId: pdfNotesEpisodeId } : episode?.id ? { episodeId: episode?.id } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )
  const { data: appointmentSummaries } = useGetAppointmentSummariesQuery(
    clientId && episode?.id
      ? {
          providerId: userId,
          patientId: clientId,
          episodeId: episode.id,
          statusOnly: true,
        }
      : skipToken,
    { skip: !enableAINotesFeatures, refetchOnMountOrArgChange: true },
  )
  const completedOrResubmittedAppts = useCompletedOrResubmittedAppts(allAppointments, episode?.id, enableDAFlexibleNote)

  const { data: pendingTransfers, isLoading: isPendingTransfersLoading } = useGetTransfersQuery(
    {
      provider_id: userId,
      patient_id: clientId!,
      status: TransferStatus.PENDING_NOTE,
    },
    {
      skip: !isDAEngageProvider || !clientId,
    },
  )

  const navigate = useNavigate()
  const { colors } = useTheme()
  const [isPDFContentInitialized, setIsPDFContentInitialized] = React.useState(false)

  const location = useLocation()
  const daInvoiceState = get(location, 'state.daInvoiceFailedModal') as
    | { show?: boolean; clientName?: string; sessionNumber?: string | number }
    | undefined
  const [showInvoiceFailedModal, setShowInvoiceFailedModal] = React.useState<boolean>(!!daInvoiceState?.show)

  useEffect(() => {
    setIsPDFContentInitialized(false)
  }, [pdfContentStartDate, pdfContentEndDate])

  useEffect(() => {
    if (
      isLoading ||
      !isSuccess ||
      !notes ||
      !sessions ||
      !clientDetails ||
      !appointmentsInfoForPDF ||
      !setNotesPDFContent ||
      isPDFContentInitialized
    ) {
      return
    }
    setIsPDFContentInitialized(true)
    setNotesPDFContent(
      ClientNotesPDF({
        isDAEngageProvider,
        appointmentsInfoForPDF,
        notes,
        sessions,
        pdfContentStartDate,
        pdfContentEndDate,
        userId,
        enableExportClientRecordsLogging,
        getNoteExternalValues: (note, session) =>
          note.content_meta_data?.name === NP_SESSION_NOTE_NAME
            ? getNpSessionNoteExternalValues(
                clientDetails?.date_of_birth,
                session ? getDateFromAppointment(session) : new Date(note.update_date),
                undefined,
                true,
              )
            : undefined,
      }),
    )
  }, [
    isLoading,
    isDAEngageProvider,
    isSuccess,
    notes,
    appointmentsInfoForPDF,
    setNotesPDFContent,
    isPDFContentInitialized,
    pdfContentStartDate,
    pdfContentEndDate,
    sessions,
    episode?.id,
    userId,
    enableExportClientRecordsLogging,
    clientDetails,
  ])

  const goToNote = useCallback(
    async (note: NoteWithNoteListItemProperties) => {
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: actions.CLIENT_NOTES_TAB_OPEN_NOTE,
        details: {
          ...(note.aiSummaryStatus && { summaryStatus: noteSummaryStatusText[note.aiSummaryStatus] }),
          noteType: sessionNoteTypes[note.note_type],
          ...(note.appointment_id && { appointmentId: note.appointment_id?.toString() }),
          button: note.status === NoteStatusStates.DRAFT ? 'Finish note' : 'View',
        },
      })

      logToSentry('noteNavigation', {
        message: 'Navigating to existing note',
        noteId: note.id,
        appointmentId: note.appointment_id,
        episodeId: episode?.id,
        clientId,
        noteType: note.note_type,
        noteStatus: note.status,
      })

      if (isDAEngageProvider && note.appointment_id && clientId) {
        const pendingChargeNavState = getPendingChargeNavState(note, sessions, pendingTransfers?.data)
        navigate(`${CLIENT_NOTE.route}?id=${note.id}`, {
          state: { note: toNavigableNote(note), ...pendingChargeNavState },
        })
      } else {
        navigate(`${CLIENT_NOTE.route}?id=${note.id}`, { state: { note: toNavigableNote(note) } })
      }
    },
    [isDAEngageProvider, clientId, sessions, pendingTransfers?.data, navigate, episode],
  )

  // Show loading if notes are loading OR if we're in redesigned UI and episode data isn't loaded yet
  if (isLoading || isPendingTransfersLoading || !isSuccess || !episode) {
    return <LoadingIndicator size={45} />
  }

  const goToNewNote = (note: any) => {
    if ([NoteTypes.INITIAL, NoteTypes.SESSION].includes(note.note_type)) {
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: actions.CLIENT_NOTES_TAB_OPEN_NOTE,
        details: {
          ...(note.aiSummaryStatus && {
            summaryStatus: noteSummaryStatusText[note.aiSummaryStatus as keyof typeof noteSummaryStatusText],
          }),
          noteType: sessionNoteTypes[note.note_type as keyof typeof sessionNoteTypes],
          ...(note.appointment_id && { appointmentId: note.appointment_id?.toString() }),
          button: 'Add note',
        },
      })
    }

    let pendingChargeNavState = {}
    if (isDAEngageProvider) {
      pendingChargeNavState = getPendingChargeNavState(note, sessions, pendingTransfers?.data)
    }

    logToSentry('noteNavigation', {
      message: 'Navigating to new note',
      noteId: note.id,
      appointmentId: note.appointment_id,
      episodeId: episode?.id,
      clientId,
      noteType: note.note_type,
      hasNoteId: !!note.id,
    })

    navigate(`${CLIENT_NEW_NOTE.route}`, { state: { note: toNavigableNote(note), ...pendingChargeNavState } })
  }

  const episodeSessions = sessions?.filter((s: any) => s.episodeId === episode?.id)

  const getInitialValues = (name = '', sessionNumber: any) => {
    let formResponses: any = []

    const clientIntake = assignments?.find((assignment: any) => {
      return (
        [
          clientEpisodeProgramConfig?.clientIntake,
          clientEpisodeProgramConfig?.oldClientIntake,
          clientEpisodeProgramConfig?.clientIntakeNonUS,
        ].includes(assignment.content.name) && assignment.episode_id === episode?.id
      )
    })
    if (clientIntake) {
      const clientIntakeResponse = get(clientIntake, 'assignment_responses.0.response')
      formResponses = [
        {
          contentName: clientIntake.content.name,
          data: clientIntakeResponse,
          responseUiSchema: clientIntake.content_meta_data.uiSchema,
        },
      ]
    }

    const previousApptId = (episodeSessions[sessionNumber - 1] || {}).appointmentId
    const previousNote = notes.find((note: any) => note.appointment_id === previousApptId)

    if (previousNote) {
      const previousNoteResponse = (previousNote as any).body || {}
      formResponses = [
        ...formResponses,
        {
          contentName: (previousNote as any).content_meta_data.name,
          data: previousNoteResponse,
          responseUiSchema: (previousNote as any).content_meta_data.meta_data.uiSchema,
        },
      ]
    }
    return populateInitialValues(formResponses, name, contents)
  }

  if (!episode) {
    return null
  }

  const {
    INITIAL_NOTE,
    SESSION_NOTE,
    GENERAL_NOTE,
    REFERRAL_NOTE,
    OLD_INITIAL_NOTE,
    OLD_SESSION_NOTE,
    OLD_GENERAL_NOTE,
    OLD_REFERRAL_NOTE,
  } = noteContentMetadataName ?? {}
  const canAddOrEditNotes = episodes?.some((epi: any) => epi.provider_patient_id === episode.provider_patient_id)
  const isDBTTeens = isDBTTeensEnabled && episode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens
  const canAddOrEditSessionNotes = canAddOrEditNotes || (isDBTTeens && openClientAccessSupported)
  const notesTitleSize = isDBTTeens ? TitleSize.MEDIUM : TitleSize.LARGE

  let sessionNotes

  const matchingProgram = lastProviderSpecificEpisode?.program_name === episode?.program_name

  if (isDBTTeens) {
    const oldNoteMetadata = contents.find((n: any) => n.name === OLD_INITIAL_NOTE || n.name === OLD_SESSION_NOTE)
    const initialNoteMetadata = contents.find((n: any) => n.name === INITIAL_NOTE) ?? oldNoteMetadata
    const sessionNoteMetadata = contents.find((n: any) => n.name === SESSION_NOTE) ?? oldNoteMetadata

    sessionNotes = episodeSessions?.map((session: any, i: number) => {
      const appointmentTimeslot = getAppointmentTimeslot({
        appointment: session,
        intl,
        showEndTime: false,
        lowercaseDayPeriod: false,
        dateTimeSeparator: ' · ',
      })
      const note = notes.find((note: any) => note.appointment_id === session.appointmentId) || {}

      const name = i === 0 ? INITIAL_NOTE : SESSION_NOTE
      const content_meta_data = i === 0 ? initialNoteMetadata : sessionNoteMetadata

      let body = (note as any).body
      if (isEmpty(note)) {
        const initialValues = getInitialValues(name, i)
        if (!isEmpty(initialValues)) {
          body = initialValues
        } else {
          const oldName = i === 0 ? OLD_INITIAL_NOTE : OLD_SESSION_NOTE
          const oldInitialValues = getInitialValues(oldName, i)
          body = !isEmpty(oldInitialValues) ? oldInitialValues : undefined
        }
      }

      const sessionType: string | undefined =
        session.sessionType ?? (i === 0 ? LTSessionTypes.DBT_TEENS_INTAKE_SESSION : undefined)

      const sessionTypeLabel = sessionType ? DBT_TEENS_SESSION_TYPE_LABELS[sessionType] : undefined
      const sessionTypeIcon = <DBTTeensSessionTypeIcon sessionType={sessionType} />
      const canAdd = session.lyraProviderId === userId
      const backgroundColor = canAdd ? undefined : colors.backgroundElement

      return {
        title: `Session ${i + 1} · ${appointmentTimeslot}`,
        appointment_id: session.appointmentId,
        content_meta_data,
        note_type: i === 0 ? NoteTypes.INITIAL : NoteTypes.SESSION,
        sessionDate: session.startDate,
        sessionNumber: session.sessionNumber,
        sessionTypeLabel,
        sessionTypeIcon,
        canAdd,
        backgroundColor,
        ...note,
        body,
      }
    })
  } else if (matchingProgram && canAddOrEditNotes) {
    sessionNotes = episodeSessions?.map((session: any, i: any) => {
      const appointmentTimeslot = getAppointmentTimeslot({ appointment: session, intl })
      const note = notes.find((note: any) => note.appointment_id === session.appointmentId) || {}

      // Prefer suggestedSessionType when present; otherwise fall back to list position. Resolve the
      // program the same way the charge does (CPR-2213) so LCT episodes classify against DA types.
      const isInitialEvaluation =
        isInitialEvaluationSessionType(
          session.suggestedSessionType,
          resolveDAEngageProgramName(episode?.program_name, isDAEngageProvider),
        ) ?? i === 0

      // A `concluded` appointment is included: it means the video session ended, not that the session
      // was closed, so it still owes an invoice and belongs to the flexible note flow (ICE-7530).
      // Reversed charges are excluded so dispute-reopened sessions keep going to the legacy addendum.
      const isUnclosedPastSession =
        enableDAFlexibleNote &&
        isDAEngageProvider &&
        isEmpty(note) &&
        isUnclosedSessionStatus(session.appointmentStatus) &&
        !session.isChargeReversed &&
        !!session.programId &&
        getDateFromAppointment(session) < new Date()

      let name: string | undefined = 'unset'
      if (isUnclosedPastSession) {
        name = DA_FLEXIBLE_NOTE_NAME
      } else if (episode.program_name === ProgramNames.AlcoholUseDisorderTherapy) {
        name = ['aud', 'sud', 'aud_and_sud'].includes(episode.assessment_set)
          ? isInitialEvaluation
            ? INITIAL_NOTE
            : SESSION_NOTE
          : isInitialEvaluation
            ? OLD_INITIAL_NOTE
            : OLD_SESSION_NOTE
      } else {
        name = isInitialEvaluation ? INITIAL_NOTE : SESSION_NOTE
      }

      const programName = getProgramNameFromId(episode?.program_id) ?? ''
      const aiSummaryStatus =
        programConfig[programName as LTProgramName]?.aiNotesEnabled &&
        (programName !== ProgramNames.MedicationManagement ||
          (programName === ProgramNames.MedicationManagement && showAINotesForLCMAndCLE)) &&
        (programName !== ProgramNames.ClinicalLeaveEvaluation ||
          (programName === ProgramNames.ClinicalLeaveEvaluation && showAINotesForLCMAndCLE)) &&
        (programName !== ProgramNames.SubstanceUseEvaluation ||
          (programName === ProgramNames.SubstanceUseEvaluation && showAINotesForLCMAndCLE)) &&
        appointmentSummaries?.find(
          (summary: AppointmentSummary) => summary.appointment_id === session.appointmentId.toString(),
        )?.status
      let body = (note as any).body
      if (isEmpty(note)) {
        const initialValues = getInitialValues(name, i)
        if (!isEmpty(initialValues)) {
          body = initialValues
        } else {
          const oldName = isInitialEvaluation ? OLD_INITIAL_NOTE : OLD_SESSION_NOTE
          const oldInitialValues = getInitialValues(oldName, i)
          body = !isEmpty(oldInitialValues) ? oldInitialValues : undefined
        }
      }
      const content_meta_data =
        contents.find((n: any) => n.name === name) ??
        contents.find((n: any) => n.name === OLD_INITIAL_NOTE || n.name === OLD_SESSION_NOTE)

      let title = `Session ${i + 1} Note`
      if (isDAEngageProvider) {
        const dateParts = session.startDate.split('-')
        const date = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]))
        title = intl.formatDate(date, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
      }

      return {
        title,
        appointment_id: session.appointmentId,
        content_meta_data,
        note_type: isInitialEvaluation ? NoteTypes.INITIAL : NoteTypes.SESSION,
        sessionDate: session.startDate,
        sessionNumber: session.sessionNumber,
        appointmentTimeslot,
        authorName: get(note, 'body.authorName'),
        ...(showAINotesFeatures && { aiSummaryStatus }),
        ...note,
        body,
      }
    })
  } else {
    sessionNotes = notes
      .filter(
        (note: any) =>
          [NoteTypes.INITIAL, NoteTypes.SESSION].includes(note.note_type) && note.status === NoteStatusStates.COMPLETED,
      )
      .map((note: any) => ({
        title: note.note_type === NoteTypes.SESSION ? 'Session note' : 'Initial Note',
        ...note,
      }))
  }

  if (isDAEngageProvider && sessionNotes) {
    sessionNotes = sessionNotes.filter((note: any) => {
      // Always show completed notes
      if (note.status === NoteStatusStates.COMPLETED) {
        return true
      }
      // For non-completed notes, include if its appointment is a closed session needing a note
      return completedOrResubmittedAppts.has(note.appointment_id)
    })
  }

  const generalNotes = notes
    .filter((note: any) => note.note_type === NoteTypes.GENERAL)
    .map((note: any) => ({
      ...note,
      title: getGeneralNoteTitle(note),
    }))
  const generalNoteMetadata = contents.find((n: any) => n.name === GENERAL_NOTE || n.name === OLD_GENERAL_NOTE)

  const mostRecentSessionNote = notes
    ? notes
        .filter((note: any) => note.status === NoteStatusStates.COMPLETED)
        .sort((a: any, b: any) => (b.update_date > a.update_date ? 1 : -1))[0]
    : {}

  let sessionNoteResponses: any = []
  if (mostRecentSessionNote) {
    const mostRecentSessionNoteResponse = (mostRecentSessionNote as any).body || {}
    sessionNoteResponses = [
      {
        contentName: (mostRecentSessionNote as any).content_meta_data.name,
        data: mostRecentSessionNoteResponse,
        responseUiSchema: (mostRecentSessionNote as any).content_meta_data.meta_data.uiSchema,
      },
    ]
  }
  const generalNotePrepopulation = populateInitialValues(sessionNoteResponses, GENERAL_NOTE, contents)

  const newGeneralNote = {
    title: generalNoteMetadata?.title,
    content_meta_data: generalNoteMetadata,
    note_type: NoteTypes.GENERAL,
    body: generalNotePrepopulation,
  }

  const referralNotes = notes
    .filter((note: any) => note.note_type === NoteTypes.REFERRAL)
    .map((note: any) => ({
      ...note,
      title: 'Care Referral Form',
    }))
  let referralNoteMetadata = contents.find((n: any) => n.name === REFERRAL_NOTE || n.name === OLD_REFERRAL_NOTE)
  referralNoteMetadata =
    isLyraSelectClient && referralNoteMetadata !== undefined
      ? addLyraSelectClientNoticeToNote(referralNoteMetadata, 'linkToZendesk')
      : referralNoteMetadata

  const newReferralNote = {
    title: referralNoteMetadata?.title,
    content_meta_data: referralNoteMetadata,
    note_type: NoteTypes.REFERRAL,
  }

  const isSelectedEpisodeAssociatedWithProvider = episode?.provider_id === userId
  const canAddGeneralAndReferralNotes =
    isSelectedEpisodeAssociatedWithProvider || (isDBTTeens && openClientAccessSupported)

  return (
    <>
      <NotesSectionContainer isDBTTeens={isDBTTeens} testID={tID('ClientNotes-sessionNotesContainer')}>
        <SubheaderContainer>
          <NotesTitle
            text={
              isDBTTeens
                ? 'Session notes'
                : `${getProgramDisplayLabel(intl, episode.program_name, 'careType')} Session Notes`
            }
            size={notesTitleSize}
            color={colors.textPrimary}
          />
        </SubheaderContainer>
        <NotesList
          notes={sessionNotes}
          onAddNoteClick={canAddOrEditSessionNotes ? goToNewNote : null}
          onFinishNoteClick={canAddOrEditSessionNotes ? goToNote : null}
          onViewNoteClick={goToNote}
          isDAEngageProvider={isDAEngageProvider}
        />
      </NotesSectionContainer>
      <NotesSectionContainer isDBTTeens={isDBTTeens} testID={tID('ClientNotes-generalNotesContainer')}>
        <SubheaderContainer>
          <NotesTitle
            text={`${getProgramDisplayLabel(intl, episode.program_name, 'careType')} General Notes`}
            size={notesTitleSize}
            color={colors.textPrimary}
          />
          {canAddGeneralAndReferralNotes && (
            <PrimaryButton
              testID={tID('ClientNotes-AddGeneralNote')}
              onPress={() => goToNewNote(newGeneralNote)}
              text='Add General Note'
              disabled={!hasRequiredNoteMetadata}
            />
          )}
        </SubheaderContainer>
        <NotesList
          notes={generalNotes}
          onViewNoteClick={goToNote}
          onFinishNoteClick={isSelectedEpisodeAssociatedWithProvider ? goToNote : null}
          showNoteTitle={true}
        />
      </NotesSectionContainer>
      <NotesSectionContainer isDBTTeens={isDBTTeens} isLastSection testID={tID('ClientNotes-referralNotesContainer')}>
        <SubheaderContainer>
          <NotesTitle text='Care Referral Form' size={notesTitleSize} color={colors.textPrimary} />
          {canAddGeneralAndReferralNotes && (
            <PrimaryButton
              testID={tID('ClientNotes-AddReferralNote')}
              onPress={() => {
                if (isDAEngageProvider) {
                  track({ event: mixpanelEvents.CLICK_REFER_TO_OTHER_CARE })
                  const zendeskURL =
                    'https://provider-support.lyrahealth.com/hc/en-us/restricted?return_to=https%3A%2F%2Fprovider-support.lyrahealth.com%2Fhc%2Fen-us%2Frequests%2Fnew%3Fticket_form_id%3D360000119987'
                  if (typeof window !== 'undefined') {
                    window.open(zendeskURL, '_blank', 'noopener,noreferrer')
                  }
                  return
                }
                goToNewNote(newReferralNote)
              }}
              text='Refer to Other Care'
              disabled={!hasRequiredNoteMetadata}
            />
          )}
        </SubheaderContainer>
        {!isDAEngageProvider && (
          <NotesList
            notes={referralNotes}
            onViewNoteClick={goToNote}
            onFinishNoteClick={isSelectedEpisodeAssociatedWithProvider ? goToNote : null}
            emptyListText='No form yet'
          />
        )}
      </NotesSectionContainer>
      <DAInvoiceFailedModal
        visible={showInvoiceFailedModal}
        onRequestClose={() => setShowInvoiceFailedModal(false)}
        clientName={daInvoiceState?.clientName || ''}
        sessionNumber={daInvoiceState?.sessionNumber}
      />
    </>
  )
}

export default ClientNotes
